// src/pages/DommageCreatePage.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import SectionCard from "../utils/SectionCard";
import CustomSelect from "../utils/CustomSelect";

import useFetchSites from "../hooks/useFetchSites";
import useFetchDeclarations from "../hooks/useFetchDeclarations";
import useFetchDommages from "../hooks/useFetchDommages";

const Row = ({ children }) => (
    <div className="gap-6 grid grid-cols-1 lg:grid-cols-2">{children}</div>
);

const Field = ({ label, children, required, mono }) => (
    <label className="block">
        <span className="text-gray-700 text-sm">
            {label} {required && <span className="text-red-500">*</span>}
        </span>
        <div className={`${mono ? "font-mono" : ""} mt-1`}>{children}</div>
    </label>
);

// ---- date helpers (mêmes que Site/Declaration) ----
const isZeroDate = (s) => !s || s === "0000-00-00";
const toInputDate = (s) => (isZeroDate(s) ? "" : s?.slice(0, 10) || "");
const fromInputDate = (s) => (s ? s : "0000-00-00");

export default function DommageCreatePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const { sites, fetchSites, loading: loadingSites, error: errorSites } = useFetchSites();
    const { declarations, fetchDeclarations, loading: loadingDeclarations, error: errorDeclarations } = useFetchDeclarations();
    const { createDommage, loading } = useFetchDommages();

    const prefillSiteUUID =
        location.state?.siteUUID || params.siteUUID || "";
    const prefillDeclaUUID =
        location.state?.declaUUID || params.declaUUID || "";

    const [dommage, setDommage] = useState({
        UUID_Site_: prefillSiteUUID || "",
        UUID_Declaration_: prefillDeclaUUID || "",

        Libelle: "",
        CODE_CRAC: "",
        Type_Risque: "",
        Etat: "OUVERT",

        Cd_Patho_1: "",
        Cd_Patho_2: "",
        Cd_Patho_3: "",
        Cd_Patho_4: "",
        Cd_Patho_5: "",
        Cd_Patho_6: "",

        Intervenant_Nom: "",

        Provision_TT: "",
        Recours_TT: "",
        Recours_PP: "",
        Reglement: "",
        Reglement_Date: "0000-00-00",
        Quitus_Transmis: false,
    });

    const [errors, setErrors] = useState({});
    const set = (name, value) => setDommage((s) => ({ ...s, [name]: value }));

    useEffect(() => {
        fetchSites();
        fetchDeclarations();
    }, []);

    useEffect(() => {
        if (!prefillDeclaUUID || !Array.isArray(declarations) || declarations.length === 0) return;
        const dec = declarations.find(d => d.UUID_ === prefillDeclaUUID);
        if (!dec) return;

        setDommage(prev => ({
            ...prev,
            UUID_Declaration_: prefillDeclaUUID,
            UUID_Site_: dec.UUID_Site_ || prev.UUID_Site_,
        }));
    }, [prefillDeclaUUID, declarations]);

    const siteOptions = useMemo(() => {
        return (sites || []).map(s => ({ id: s.UUID_, label: s.Nom }));
    }, [sites]);

    const declaOptions = useMemo(() => {
        const list = (declarations || []).filter(d =>
            dommage.UUID_Site_ ? d.UUID_Site_ === dommage.UUID_Site_ : true
        );
        return list.map(d => ({
            id: d.UUID_,
            label: d.Libelle || d.Code_Procedure || d.UUID_,
            UUID_Site_: d.UUID_Site_,
        }));
    }, [declarations, dommage.UUID_Site_]);

    const siteLabel =
        siteOptions.find(o => o.id === dommage.UUID_Site_)?.label
        || declaOptions.find(o => o.id === dommage.UUID_Declaration_)?.Site_Nom
        || "";

    const declaLabel =
        declaOptions.find(o => o.id === dommage.UUID_Declaration_)?.label || "";

    console.log(declaLabel, dommage.UUID_Declaration_);

    const validate = () => {
        const e = {};
        if (!dommage.UUID_Site_?.trim()) e.UUID_Site_ = "Site requis";
        if (!dommage.UUID_Declaration_?.trim())
            e.UUID_Declaration_ = "Déclaration requise";
        if (!dommage.Libelle?.trim()) e.Libelle = "Libellé requis";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;

        const { UUID_Site_, ...dommageFormatted } = dommage;

        const payload = {
            ...dommageFormatted,
            Reglement_Date: fromInputDate(toInputDate(dommage.Reglement_Date)),
            Provision_TT:
                dommage.Provision_TT === "" ? "" : Number(dommage.Provision_TT),
            Recours_TT:
                dommage.Recours_TT === "" ? "" : Number(dommage.Recours_TT),
            Recours_PP:
                dommage.Recours_PP === "" ? "" : Number(dommage.Recours_PP),
            Reglement: dommage.Reglement === "" ? "" : Number(dommage.Reglement),
        };

        // on retire des étiquettes temporaires si jamais tu en ajoutes ailleurs
        // (ici rien à retirer de spécial)

        const created = await createDommage(payload);
        if (created?.UUID_) {
            navigate(`/dommages/${created.UUID_}`);
        } else {
            // fallback
            navigate(`/dommages`);
        }
    };

    const lockAll = Boolean(prefillDeclaUUID);
    const lockSite = lockAll || Boolean(prefillSiteUUID);
    const lockDecla = lockAll;

    return (
        <div className="mx-auto p-4 lg:p-8">
            {/* header */}
            <div className="top-4 lg:top-8 z-20 sticky flex justify-center items-center bg-gray-900 drop-shadow-xl mb-6 px-8 rounded-lg w-full min-h-20">
                <div className="flex justify-between items-center px-4 md:px-0 py-3 w-full h-full">
                    <Link
                        to={
                            prefillSiteUUID
                                ? `/sites/${prefillSiteUUID}`
                                : prefillDeclaUUID
                                    ? `/declarations/${prefillDeclaUUID}`
                                    : "/dommages"
                        }
                        className="inline-flex items-center gap-2 text-white hover:text-orange-500 transition duration-300 ease-in-out"
                    >
                        <i className="fa-arrow-left fa-solid" />
                        <span>
                            {prefillSiteUUID
                                ? "Revenir au Site"
                                : prefillDeclaUUID
                                    ? "Revenir à la Déclaration"
                                    : "Revenir à la liste des Dommages"}
                        </span>
                    </Link>

                    <button
                        onClick={onSave}
                        disabled={loading}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white 
              ${loading
                                ? "bg-orange-400 cursor-not-allowed"
                                : "bg-orange-600 hover:bg-orange-500 transition duration-300 ease-in-out cursor-pointer"
                            }`}
                    >
                        <i className="fa-solid fa-floppy-disk" />
                        <span>Sauvegarder le dommage</span>
                    </button>
                </div>
            </div>

            {/* bandeau titre */}
            <div className="flex items-end gap-4 bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 mt-4 p-4 px-6 rounded-lg text-white">
                <p className="space-x-2 font-bold text-2xl">
                    <i className="fa-solid fa-fire-extinguisher fa-sm" />
                    <span>{dommage.Libelle || "Création d'un nouveau Dommage"}</span>
                </p>
            </div>

            <div className="space-y-6 mt-6">
                {/* LIGNE 1 : Rattachement & Détails */}
                <Row>
                    <SectionCard title="Rattachement" icon="fa-link">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Site" required>
                                <CustomSelect
                                    options={siteOptions}
                                    isNested
                                    isDisabled={lockSite}
                                    value={siteLabel || (loadingSites ? "Chargement..." : "Sélectionner…")}
                                    onChange={(opt) => {
                                        set("UUID_Site_", opt?.id || "");
                                    }}
                                    className={`w-full ${errors.UUID_Site_ ? "border-red-400" : ""} ${prefillDeclaUUID ? "bg-orange-100" : ""}`}
                                />
                                {errors.UUID_Site_ && <p className="mt-1 text-red-500 text-xs">{errors.UUID_Site_}</p>}
                                {errorSites && <p className="mt-1 text-red-500 text-xs">Erreur de chargement des sites : {errorSites}</p>}
                            </Field>

                            <Field label="Déclaration" required>
                                <CustomSelect
                                    options={declaOptions}
                                    isNested
                                    isDisabled={lockDecla}
                                    value={declaLabel || (loadingDeclarations ? "Chargement..." : "Sélectionner…")}
                                    onChange={(opt) => {
                                        set("UUID_Declaration_", opt?.id || "");
                                    }}
                                    className={`w-full ${errors.UUID_Declaration_ ? "border-red-400" : ""} ${prefillDeclaUUID ? "bg-orange-100" : ""}`}
                                />
                                {errors.UUID_Declaration_ && <p className="mt-1 text-red-500 text-xs">{errors.UUID_Declaration_}</p>}
                                {errorDeclarations && <p className="mt-1 text-red-500 text-xs">Erreur de chargement des declarations : {errorDeclarations}</p>}
                            </Field>
                        </div>
                    </SectionCard>

                    <SectionCard title="Détails du dommage" icon="fa-circle-info">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Libellé" required>
                                <input
                                    className={`px-3 py-2 border rounded w-full ${errors.Libelle ? "border-red-400" : "border-gray-300"
                                        }`}
                                    value={dommage.Libelle}
                                    onChange={(e) => set("Libelle", e.target.value)}
                                    placeholder="Ex: Infiltration toiture bâtiment B"
                                />
                                {errors.Libelle && (
                                    <p className="mt-1 text-red-500 text-xs">{errors.Libelle}</p>
                                )}
                            </Field>

                            <Field label="Code CRAC" mono>
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.CODE_CRAC}
                                    onChange={(e) => set("CODE_CRAC", e.target.value)}
                                />
                            </Field>

                            <Field label="Type de Risque">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.Type_Risque}
                                    onChange={(e) => set("Type_Risque", e.target.value)}
                                />
                            </Field>

                            <Field label="État">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.Etat}
                                    onChange={(e) => set("Etat", e.target.value)}
                                    placeholder="OUVERT / ... "
                                />
                            </Field>
                        </div>
                    </SectionCard>
                </Row>

                {/* LIGNE 2 : Pathologies & Intervenant */}
                <Row>
                    <SectionCard title="Pathologies identifiées" icon="fa-stethoscope">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-3">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <Field key={i} label={`Code Pathologie ${i}`}>
                                    <input
                                        className="px-3 py-2 border border-gray-300 rounded w-full"
                                        value={dommage[`Cd_Patho_${i}`]}
                                        onChange={(e) => set(`Cd_Patho_${i}`, e.target.value)}
                                    />
                                </Field>
                            ))}
                        </div>
                    </SectionCard>

                    <SectionCard title="Intervenants & Références" icon="fa-people-group">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Intervenant — Nom">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.Intervenant_Nom}
                                    onChange={(e) => set("Intervenant_Nom", e.target.value)}
                                />
                            </Field>
                        </div>
                    </SectionCard>
                </Row>

                {/* LIGNE 3 : Financier / Recours & Règlements */}
                <Row>
                    <SectionCard title="Récapitulatif financier" icon="fa-file-invoice-dollar">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Provision Totale (€)">
                                <input
                                    type="number"
                                    step="1"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.Provision_TT}
                                    onChange={(e) => set("Provision_TT", e.target.value)}
                                />
                            </Field>
                            <Field label="Recours Total (€)">
                                <input
                                    type="number"
                                    step="1"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.Recours_TT}
                                    onChange={(e) => set("Recours_TT", e.target.value)}
                                />
                            </Field>
                            <Field label="Recours Prévu (€)">
                                <input
                                    type="number"
                                    step="1"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.Recours_PP}
                                    onChange={(e) => set("Recours_PP", e.target.value)}
                                />
                            </Field>
                        </div>
                    </SectionCard>

                    <SectionCard title="Règlements / Quitus" icon="fa-scale-balanced">
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Règlement (€)">
                                <input
                                    type="number"
                                    step="1"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={dommage.Reglement}
                                    onChange={(e) => set("Reglement", e.target.value)}
                                />
                            </Field>
                            <Field label="Règlement — Date">
                                <input
                                    type="date"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(dommage.Reglement_Date)}
                                    onChange={(e) => set("Reglement_Date", fromInputDate(e.target.value))}
                                />
                            </Field>
                            <div className="flex items-center gap-3 mt-2">
                                <input
                                    id="quitus"
                                    type="checkbox"
                                    className="w-4 h-4"
                                    checked={!!dommage.Quitus_Transmis}
                                    onChange={(e) => set("Quitus_Transmis", e.target.checked)}
                                />
                                <label htmlFor="quitus" className="text-gray-700 text-sm">
                                    Quitus transmis
                                </label>
                            </div>
                        </div>
                    </SectionCard>
                </Row>
            </div>
        </div>
    );
}
